import { getIdToken } from './firebase.js';
import type { OutletTheme } from './theme.js';
import type { OutletSubscriptionInfo } from './subscription.js';

const API_BASE = '/api';

/**
 * Gateway errors arrive as { success:false, error: { code, message } }
 * (older endpoints may send `error` as a plain string). Passing the object
 * straight into `new ApiError(...)` renders as "[object Object]" — so always
 * extract the human-readable message here.
 */
function apiErrorText(data: any, fallback: string): string {
  const err = data?.error;
  if (!err || typeof err !== 'object') return typeof err === 'string' && err ? err : fallback;
  return err.message || err.code || fallback;
}


// ── Stock management types (Admin write / Staff read) ──────────────────

export interface StockTypeOption {
  id: string;
  code: string;
  name: string;
  /** TypeRecord.limit_value — percent-above-cost for price categories. */
  limitValue?: string;
}

export interface StockListImage {
  url: string;
  imageNo: number;
}

export interface StockListItem {
  id: string;
  code: string;
  name: string;
  description?: string;
  qoh: number;
  qohString: string;
  price: number;
  typeName: string;
  imageUrl?: string;
  thumbnailUrl?: string;
}

export interface StockPriceCategory {
  typeId: string;
  code: string;
  name: string;
  percentAboveCost: string;
  price: number;
}

export interface StockPackRecord {
  packType: StockTypeOption;
  packDescription: string;
  packSize: number;
  sellingPrice: number;
  membershipPrice: number;
  maxDiscountPercentage: number;
  percentAboveCost: number;
  barcode: string;
  priceByCategory: StockPriceCategory[];
}

export interface StockTaxCharge {
  typeId: string;
  code: string;
  name: string;
  parentId: string;
  percentage: number;
  fixedValue: number;
  fromDate: number;
  toDate: number;
}

export interface StockComponent {
  componentId: string;
  quantity: number;
  costFactor: number;
  packTypeId: string;
  packTypeCode?: string;
  packSize?: number;
}

export interface StockSalesRep {
  id: string;
  name: string;
  pricePercent: number;
}

export interface StockDetail {
  id: string;
  code: string;
  name: string;
  regNo: string;
  colour: string;
  detail: string;
  barCode: string;
  brand: string;
  genericName: string;
  strength: string;
  remark: string;
  alternateName: string;
  additionalPacking: string;
  eInvoiceClassification: string;
  displayName: string;
  fcc: string;
  type: { id: string; name: string };
  parentId: string;
  rewardPointFactor: number;
  reorderLevel: number;
  reorderQty: number;
  isPoison: boolean;
  isPseudoPoison: boolean;
  noOfImages: number;
  qoh: number;
  qohHold: number;
  weightedAvgCost: number;
  latestCost: number;
  maxCost: number;
  averageCostPpos: number;
  promotionalDiscount: number;
  dispensingInstruction: string;
  drugWarning: string;
  minProfitMargin: string;
  toConvertPackSize: boolean;
  synonymTo: string;
  defaultSellingPack?: StockTypeOption;
  defaultPurchasePack?: StockTypeOption;
  attributes: Array<{ id: string; code: string; name: string; parentId: string }>;
  packRecords: StockPackRecord[];
  taxAndCharges: StockTaxCharge[];
  salesPromotionList: Array<{ fromDate: number; toDate: number; discountPercentage: number }>;
  componentList: StockComponent[];
  shelfList: Array<{ id: string; code: string; name: string }>;
  salesReps: StockSalesRep[];
}

/** Full-record save payload — built from a loaded StockDetail so that taxes,
 *  promotions, components, shelves and costs round-trip (required by the
 *  server-side replace semantics). */
export interface StockSavePayload extends Partial<StockDetail> {
  typeId?: string;
}

export interface StockMeta {
  serverKey: {
    autoGenerateItemCode: boolean;
    allowDuplicateCode: boolean;
    adminCanModifyQoh: boolean;
    enableEInvoice: boolean;
    hasGst: boolean;
    enableFractionalQuantity: boolean;
    enableFractionalPackSize: boolean;
    noOfDecimalPointForPrice: number;
  };
  stockTypes: StockTypeOption[];
  adjustTypes: StockTypeOption[];
  priceCategories: StockTypeOption[];
  categories: Array<{ id: string; parentId: string; description: string }>;
}

export interface StockImageEntry {
  id: string;
  stockId: string;
  imageNo: number;
  format: string;
  width: number;
  height: number;
  fileSize: number;
  hasThumbnail: boolean;
  filePath: string;
  thumbPath: string;
}

// ── Reports (Admin-only "Other Reports") ───────────────────────────────
// Menu comes from the server's script/ServerSideMenu.xml (via the gateway).

export interface ReportField {
  label: string;
  /** date | combo | select | string | text | integer | number */
  type: string;
  /** SQL bind name, e.g. "@fromdate" */
  param: string;
  /** begin | end (time-of-day semantics applied server-side for dates) */
  startFlag: string;
  defaultValue: string;
  /** "value:label,value:label" for combo fields */
  list: string;
  /** opscustomer | opsstockrecord | opscompany | opsshelf | ... for select fields */
  lookup: string;
}

export interface ReportAction {
  /** Unique id — the menu path, e.g. "Sales/By Cashier/Sales By Cashier" */
  id: string;
  name: string;
  script: string;
  sheetType: string;
  cmdBefore: string;
  userParam: string;
  dialogTitle: string;
  fields: ReportField[];
}

export interface ReportMenuNode {
  name: string;
  role: string;
  menus: ReportMenuNode[];
  actions: ReportAction[];
}

export interface ReportLookupItem {
  id: string;
  label: string;
  columns: string[];
}

export interface ReportRunPayload {
  id: string;
  outputType: 'pdf' | 'pdfLandscape' | 'xls';
  params: Array<{ param: string; value: string }>;
}

// ── Member / Company back-office (staff panels) ────────────────────────

export type CompanyType = 'opscustomer' | 'opscompany' | 'opssalesrep' | 'opspromoter';

export interface MemberDetail {
  id: string;
  code: string;
  name: string;
  ic?: string;
  email?: string;
  telNo?: string;
  faxNo?: string;
  contact?: string;
  address?: string;
  postcode?: string;
  state?: string;
  country?: string;
  city?: string;
  idType?: string;
  notes?: string;
  allergy?: string;
  medicalHistory?: string;
  longTermMedication?: string;
  tin?: string;
  dob?: number;
  gender?: string;
  bloodType?: string;
  race?: string;
  religion?: string;
  maritalStatus?: string;
  discount?: number;
  creditTerm?: number;
  debitTerm?: number;
  creditLimit?: number;
  purposeOfSales?: string;
  isTaxable?: boolean;
  canSellPoison?: boolean;
  enrollNumber?: string;
  pointBalance?: number;
}

export interface MemberPointRow {
  rowId: number;
  date: number;
  receiptNo?: string;
  amount?: number;
  points?: number;
  balance?: number;
  where?: string;
}

export interface CompanyDetail {
  id: string;
  code: string;
  name: string;
  ic?: string;
  email?: string;
  telNo?: string;
  faxNo?: string;
  contact?: string;
  address?: string;
  postcode?: string;
  state?: string;
  country?: string;
  city?: string;
  idType?: string;
  notes?: string;
  tin?: string;
  dob?: number;
  gender?: string;
  race?: string;
  religion?: string;
  maritalStatus?: string;
  discount?: number;
  creditTerm?: number;
  debitTerm?: number;
  creditLimit?: number;
  purposeOfSales?: string;
  isTaxable?: boolean;
  canSellPoison?: boolean;
}

export interface CompanyListRow {
  id: string;
  /** Desktop list description — "Name Code" (Search row column 1). */
  description: string;
}

export interface HistoryRow {
  date: number;
  transactionId?: string;
  refNo?: string;
  itemCode?: string;
  item?: string;
  qty?: number;
  pack?: string;
  bonusQty?: number;
  bonusPack?: string;
  price?: number;
  netPrice?: number;
  amount?: number;
  discount?: number;
  qohString?: string;
  brand?: string;
  genericName?: string;
  batchNo?: string;
  expiryDate?: number;
  remark?: string;
  location?: string;
  who?: string;
  dateCreated?: number;
}

export interface AccountRow {
  date: number;
  refNo?: string;
  debit?: number;
  credit?: number;
  remark?: string;
}

export interface SupplyRow {
  id: string;
  code?: string;
  name?: string;
  costPrice?: number;
  qty?: number;
  qtyBonus?: number;
  qohString?: string;
  reorderQty?: number;
  bonusScheme?: string;
}

export interface QuotationRow {
  itemCode?: string;
  item?: string;
  qohString?: string;
  price?: number;
  qty?: number;
  pack?: string;
  bonusQty?: number;
  bonusPack?: string;
  date?: number;
  salesRep?: string;
  remark?: string;
}

export interface InvoiceRow {
  date?: number;
  id?: string;
  refNo?: string;
  term?: string;
  total?: number;
  paidOn?: string;
  paidBy?: string;
  payRef?: string;
  totalPaid?: number;
}

export interface SummaryRow {
  itemId?: string;
  item?: string;
  month?: string;
  qty?: number;
  packSizes?: string;
}

// ── Trade (Sales / Wholesales / Purchases) types ───────────────────────

export interface TradeServerKey {
  storeCode: string;
  storeName: string;
  salesTypes: { cash: string; credit: string };
  rounding: {
    sumToNearest: number;
    truncateSum: boolean;
    round5cents: boolean;
    round5centsWholesales: boolean;
  };
  wholesale: { useCost: boolean; referenceCost: string; enableCreditLimitCheck: boolean };
  purchase: { defaultCostPrice: string; allowEditInvoiceRecord?: boolean };
  gstTypeId: string;
  payTypes: Array<{ id: string; name: string }>;
}

export interface PosPack {
  packTypeId: string;
  packTypeCode: string;
  packDescription: string;
  packSize: number;
  sellingPrice: number;
  membershipPrice: number;
  maxDiscountPercentage: number;
  barcode: string;
}

export interface PosStockItem {
  id: string;
  code: string;
  name: string;
  displayName?: string;
  detail?: string;
  barCode?: string;
  typeName?: string;
  qoh?: number;
  qohString?: string;
  qohHold?: number;
  weightedAvgCost?: number;
  latestCost?: number;
  maxCost?: number;
  promotionalDiscount?: number;
  rewardPointFactor?: number;
  isPoison?: boolean;
  packRecords: PosPack[];
  taxAndCharges?: Array<{ typeId: string; name: string; percentage: number; fromDate: number; toDate: number }>;
  // code lookup extras
  pack?: string;
  batchNo?: string;
  expiryDate?: number;
  receiveDetailId?: string;
  // search extras
  imageUrl?: string;
  thumbnailUrl?: string;
  description?: string;
}

export interface TradeLineIn {
  stockId: string;
  pack: string;
  quantity: number;
  bonusQty?: number;
  bonusPack?: string;
  price: number;
  listPrice?: number;
  discount?: number;
  tax?: number;
  value?: number;
  costValue?: number;
  batchNo?: string;
  expiryDate?: number;
  receiveDetailId?: string;
  instruction?: string;
  srp?: number;
  updateAllSellingPrice?: boolean;
}

export interface TradePaymentIn {
  payById: string;
  amount: number;
  tendered?: number;
  reference?: string;
}

export interface TradeSaleIn {
  /** Existing posted sale id — when set the store updates (edits) that record. */
  id?: string;
  type: 'cash' | 'credit';
  items: TradeLineIn[];
  payments?: TradePaymentIn[];
  customerId?: string;
  refNo?: string;
  note?: string;
  remark?: string;
  /** Channel of origin carried from a recalled hold (web | self-service | desktop). */
  origin?: string;
  term?: number;
  deliverToId?: string;
  promoterId?: string;
  pharmacistId?: string;
  transactionDate?: number;
  discountAtTotal?: number;
  rounding?: number;
}

export interface TradePurchaseIn {
  /** Existing posted receive id — when set the store updates (edits) that record. */
  id?: string;
  items: TradeLineIn[];
  supplierId?: string;
  refNo?: string;
  note?: string;
  term?: number;
  salesRepId?: string;
  pharmacistId?: string;
  supplierSalesId?: string;
  eInvoiceUrl?: string;
  transactionDate?: number;
}

export interface TradeDocLine {
  stockId: string;
  code: string;
  name: string;
  barCode?: string;
  pack: string;
  bonusPack?: string;
  quantity: number;
  bonusQty?: number;
  price?: number;
  listPrice?: number;
  discount?: number;
  srp?: number;
  value: number;
  tax?: number;
  costValue?: number;
  batchNo?: string;
  expiryDate?: number;
  receiveDetailId?: string;
  instruction?: string;
  qoh?: number;
  packSize?: number;
  packDesc?: string;
}

export interface TradePaymentRow {
  paidById: string;
  paidByCode: string;
  paidByName: string;
  amount: number;
  tendered: number;
  reference: string;
  points: number;
  date: number;
}

export interface TradeSalesRow {
  id: string;
  seqNo: number;
  date: number;
  dateCreated?: number;
  total: number;
  tax: number;
  rounding: number;
  discountAtTotal: number;
  term: number;
  refNo: string;
  note: string;
  remark?: string;
  /** Channel of origin: web | self-service | desktop. */
  origin?: string;
  salesTypeId: string;
  customerId: string;
  customerCode?: string;
  customerName?: string;
  customerEmail?: string;
  promoterId?: string;
  promoterName?: string;
  deliverToName?: string;
  pharmacistName?: string;
  userId?: string;
  userName?: string;
  terminalName?: string;
  payments: TradePaymentRow[];
  lines?: TradeDocLine[];
}

export interface TradePurchaseRow {
  id: string;
  date: number;
  dateCreated?: number;
  refNo: string;
  note: string;
  remark?: string;
  term: number;
  supplierId: string;
  supplierCode?: string;
  supplierName?: string;
  salesRepId?: string;
  salesRepName?: string;
  supplierSalesId?: string;
  eInvoiceUrl?: string;
  total: number;
  itemCount: number;
  lines?: TradeDocLine[];
}

// ── Accounting (expenses, pay-by accounts, settlements, reports) ───────

export interface AccountingPayBy {
  id: string;
  name: string;
  accountEntityId: number;
  kind: string;
}

export interface AccountingCategory {
  id: string;
  name: string;
}

export interface AccountingAccountRef {
  id: string;
  name: string;
  /** Chart of Accounts fields (gateway joins the Account row to its Types). */
  code?: string;
  typeId?: string;
  typeName?: string;
  /** Top-level bucket: Assets | Liabilities | Owner's Equity | Revenue | Expenses. */
  groupName?: string;
  sectionName?: string;
  /** False for categories the store cannot write through the browser ops (Gain). */
  writable?: boolean;
}

/** Selectable leaf category for the "Add account" form. */
export interface AccountingAccountType {
  id: string;
  name: string;
  groupName?: string;
  sectionName?: string;
  writable?: boolean;
}

export interface AccountingMeta {
  payBy: AccountingPayBy[];
  categories: AccountingCategory[];
  accounts: AccountingAccountRef[];
  accountTypes?: AccountingAccountType[];
  isAdmin: boolean;
  today: number;
}

export interface ExpenseRow {
  id: string;
  date: number;
  categoryId: string;
  categoryName: string;
  description: string;
  payee: string;
  paidByTypeId: number;
  paidByName: string;
  amount: number;
  taxAmount: number;
  reference: string;
  remark: string;
  createdBy: string;
  dateCreated: number;
}

export interface ExpenseListResult {
  rows: ExpenseRow[];
  totals: { count: number; gross: number; tax: number; net: number };
  from: number;
  to: number;
}

export interface ExpenseInput {
  id?: string;
  date: number;
  categoryId: string;
  categoryName: string;
  description: string;
  payee?: string;
  paidById?: string;
  paidByName?: string;
  amount: number;
  taxAmount?: number;
  reference?: string;
  remark?: string;
}

export interface AgingInvoice {
  id: string;
  refNo: string;
  partyId: number;
  partyName: string;
  date: number;
  dueDate: number;
  invoiceAmount: number;
  paid: number;
  outstanding: number;
  daysOverdue: number;
}

export interface AgingParty {
  partyId: number;
  partyName: string;
  total: number;
  current: number;
  d1_30: number;
  d31_60: number;
  d61_90: number;
  d90p: number;
  count: number;
}

export interface AgingResult {
  asOf: number;
  parties: AgingParty[];
  invoices: AgingInvoice[];
}

export interface PartyPaymentAllocation {
  invoiceId: string;
  amount: number;
  remark?: string;
}

export interface PartyPaymentInput {
  partyId: string;
  paidById: string;
  date: number;
  reference?: string;
  remark?: string;
  allocations: PartyPaymentAllocation[];
}

export interface PartyPaymentResult {
  journalId: string;
  total: number;
  outstanding: number;
}

export interface SettlementTransfer {
  id: number;
  date: number;
  kind: string;
  fromAccountId: number;
  toAccountId: number;
  /** Account name resolved server-side; empty when the account no longer exists. */
  fromAccountName: string;
  toAccountName: string;
  amount: number;
  feeAmount: number;
  paidByTypeId: number;
  provider: string;
  reference: string;
  remark: string;
  createdBy: string;
  dateCreated: number;
}

export interface SettlementCollected {
  paidById: number;
  name: string;
  kind: string;
  accountId: number;
  amount: number;
  entries: number;
}

export interface SettlementOnline {
  provider: string;
  collected: number;
  fee: number;
  net: number;
  entries: number;
}

export interface SettlementBanked {
  paidById: number;
  provider: string;
  amount: number;
  fee: number;
  entries: number;
}

export interface SettlementsResult {
  transfers: SettlementTransfer[];
  collected: SettlementCollected[];
  online: SettlementOnline[];
  banked: SettlementBanked[];
  from: number;
  to: number;
}

export interface AccountStatementRow {
  id: number;
  name: string;
  kind: string;
  opening: number;
  inflow: number;
  outflow: number;
  closing: number;
}

export interface ExpenseSummaryResult {
  categories: Array<{ category: string; entries: number; gross: number; tax: number; net: number }>;
  months: Array<{ month: string; gross: number; net: number }>;
}

export interface ProfitLossResult {
  revenue: number;
  outputTax: number;
  cogs: number;
  grossProfit: number;
  expenses: number;
  fees: number;
  netProfit: number;
  expensesByCategory: Array<{ category: string; net: number }>;
}

export interface CashMovementResult {
  receipts: Array<{ method: string; amount: number }>;
  payments: Array<{ method: string; amount: number }>;
  expenses: Array<{ method: string; amount: number }>;
  inTotal: number;
  outTotal: number;
  net: number;
}

export interface TrendResult {
  rows: Array<{ month: string; sales: number; cogs: number; expenses: number; grossProfit: number; netProfit: number }>;
}

/** One month of the financial-dashboard trend (all P&L figures net of GST). */
export interface DashboardMonth {
  month: string;
  sales: number;
  cogs: number;
  expenses: number;
  fees: number;
  grossProfit: number;
  netProfit: number;
}

/** Compact AR/AP ageing totals (no per-invoice array). */
export interface DashboardAgingSummary {
  total: number;
  current: number;
  d1_30: number;
  d31_60: number;
  d61_90: number;
  d90p: number;
  /** Number of parties with an outstanding balance. */
  parties: number;
}

export interface DashboardResult {
  from: number;
  to: number;
  chartFrom: number;
  asOf: number;
  months: DashboardMonth[];
  ar: DashboardAgingSummary;
  ap: DashboardAgingSummary;
  /** Null when the outlet's stock summary could not be read. */
  stock: DashboardStockSummary | null;
}

/** Stock-control totals for the financial dashboards. */
export interface DashboardStockSummary {
  /** Active (non-deleted, named) stock items. */
  skus: number;
  /** Items at or below zero QOH. */
  outOfStock: number;
  /** Items above zero but at/below their reorder level. */
  lowStock: number;
  /** Items with negative QOH (a data-integrity warning). */
  negativeStock: number;
  /** Items with QOH > 0 and no sale in the last 90 days. */
  slowMoving: number;
  /** QOH × weighted average cost, in RM. */
  stockValue: number;
}

export type AgingBucket = 'all' | 'current' | 'd1_30' | 'd31_60' | 'd61_90' | 'd90p';
export type StockControlKind = 'value' | 'low' | 'out' | 'negative' | 'slow';

export interface DashboardAgingParty {
  partyId: number;
  partyName: string;
  /** Outstanding inside the clicked bucket. */
  amount: number;
  /** Party's whole outstanding balance. */
  total: number;
  /** Invoices inside the clicked bucket. */
  count: number;
}

export interface DashboardAgingInvoice {
  id: string;
  refNo: string;
  partyId: number;
  partyName: string;
  date: number;
  dueDate: number;
  invoiceAmount: number;
  paid: number;
  outstanding: number;
  daysOverdue: number;
}

export interface DashboardAgingDetail {
  kind: 'ar' | 'ap';
  bucket: AgingBucket;
  asOf: number;
  total: number;
  parties: DashboardAgingParty[];
  invoices: DashboardAgingInvoice[];
}

export interface DashboardStockItem {
  id: string;
  code: string;
  name: string;
  typeName: string;
  qoh: number;
  reorderLevel: number;
  unitCost: number;
  stockValue: number;
  latestSalesDate: number;
  latestReceiveDate: number;
}

export interface DashboardStockDetail {
  kind: StockControlKind;
  asOf: number;
  total: number;
  items: DashboardStockItem[];
}

class ApiClient {
  private sessionKey: string | null = null;

  setSessionKey(key: string | null) {
    this.sessionKey = key;
  }

  /**
   * Fetch wrapper that automatically refreshes the Firebase ID token
   * and retries ONCE when the server returns 401 (token expired).
   */
  private async fetchWithAuthRefresh(
    url: string,
    init: RequestInit,
    _retried = false,
  ): Promise<Response> {
    // Build headers — merge provided init.headers with session key
    const headers = new Headers(init.headers);
    if (this.sessionKey) {
      headers.set('X-Session-Key', this.sessionKey);
    }
    // Endpoints that count on session auth alone must still carry the
    // Firebase token: it is what lets the gateway auto-recover a lost
    // session (gateway restart) instead of bouncing the user to login.
    if (!headers.has('Authorization')) {
      const cachedToken = await getIdToken(false);
      if (cachedToken) headers.set('Authorization', `Bearer ${cachedToken}`);
    }

    const res = await fetch(url, { ...init, headers });

    // If not 401, or this is already the retry, return as-is
    if (res.status !== 401 || _retried) {
      return res;
    }

    // Token expired — try to get a fresh one
    const freshToken = await getIdToken(true);
    if (!freshToken) {
      // No user signed in — can't refresh, return original 401
      return res;
    }

    // Retry with the fresh token. The updated headers MUST travel into the
    // retry — recursing with the original `init` would send the stale token
    // again and the refresh would be pointless.
    headers.set('Authorization', `Bearer ${freshToken}`);
    return this.fetchWithAuthRefresh(url, { ...init, headers }, true);
  }

  /**
   * Silent re-login: trade a fresh Firebase ID token for a new gateway session
   * key (same exchange as the login screen). Used when the stored session key
   * was rejected — e.g. the gateway restarted and dropped its session store.
   * Updates the API client and notifies the app ("ppos-session-refreshed")
   * so reactive state + the sessionStorage cache follow along.
   */
  private async silentRelogin(): Promise<boolean> {
    try {
      const idToken = await getIdToken(true);
      if (!idToken) return false;
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ idToken }),
      });
      if (!res.ok) return false;
      const data = await res.json().catch(() => null);
      const sessionKey = data?.data?.sessionKey;
      if (!sessionKey) return false;
      this.setSessionKey(sessionKey);
      window.dispatchEvent(new CustomEvent('ppos-session-refreshed', {
        detail: {
          sessionKey,
          email: data?.data?.email,
          ownedDBs: data?.data?.ownedDBs,
        },
      }));
      return true;
    } catch {
      return false;
    }
  }

  /**
   * JSON request helper for the standard session-authenticated endpoints.
   * The Firebase ID token is sent alongside the session key so the gateway
   * can auto-recover a lost session; if it still answers 401, the request is
   * retried once behind a silent re-login (fresh session key + re-established
   * server sessions) instead of forcing the user to log out and back in.
   */
  private async request<T = any>(
    method: string,
    path: string,
    body?: unknown,
    _retried = false,
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (this.sessionKey) {
      headers['X-Session-Key'] = this.sessionKey;
    }
    const idToken = await getIdToken(false);
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401 && !_retried && path !== '/auth/login') {
      if (await this.silentRelogin()) {
        return this.request<T>(method, path, body, true);
      }
    }

    const data = await res.json();
    if (!res.ok) {
      throw new ApiError(res.status, apiErrorText(data, 'Request failed'));
    }
    // The gateway answers HTTP 200 even for logical failures, so an expired
    // session arrives as {success:false, error:{code:"SESSION_EXPIRED"}}
    // instead of a 401. Treat it like one: re-login once and retry.
    const errCode = data?.error?.code;
    if (
      data?.success === false &&
      (errCode === 'SESSION_EXPIRED' || errCode === 'UNAUTHORIZED') &&
      !_retried &&
      path !== '/auth/login' &&
      path !== '/auth/logout'
    ) {
      if (await this.silentRelogin()) {
        return this.request<T>(method, path, body, true);
      }
    }
    return data;
  }

  /**
   * Same as request(), but treats the gateway envelope as authoritative:
   * HTTP 200 with {success:false, error:{code,message}} is a FAILURE.
   * Action endpoints (publish/save/settings) must use this — otherwise the
   * UI reports success and closes its dialog while nothing was written.
   */
  private async requestChecked<T = any>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<{ success: boolean; data: T }> {
    const res = await this.request<{ success?: boolean; data?: T }>(method, path, body);
    if (!res || res.success === false || res.data === undefined) {
      throw new ApiError(200, apiErrorText(res, 'Request failed'));
    }
    return res as { success: boolean; data: T };
  }

  /**
   * JSON request helper for endpoints that require a Firebase Bearer token.
   * Automatically refreshes the token and retries once on 401.
   */
  private async requestWithAuth<T = any>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const idToken = await getIdToken(true);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: idToken ? `Bearer ${idToken}` : '',
    };

    const res = await this.fetchWithAuthRefresh(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await res.json();
    if (!res.ok) {
      throw new ApiError(res.status, apiErrorText(data, 'Request failed'));
    }
    return data;
  }

  async login(idToken: string) {
    return this.request<{
      success: boolean;
      data: {
        sessionKey: string;
        email: string;
        billingEnabled?: boolean;
        ownedDBs: Array<{
          code: string;
          name: string;
          masterDB: string;
          transDB: string;
          hostAddress: string;
          hostPort: number;
          hostName: string;
          outletName: string;
          accessAs: string[];
          renewalOnly?: boolean;
          expiredReasons?: string[];
          subscription?: OutletSubscriptionInfo;
        }>;
        storeCount: number;
      };
    }>('POST', '/auth/login', { idToken });
  }

  /**
   * Force re-discovery of the user's stores across the currently connected
   * servers. Called when the gateway announces a server coming back online so
   * outlets it serves become visible without logging out and back in.
   */
  async refreshStores(idToken: string) {
    return this.request<{
      success: boolean;
      data: {
        email: string;
        billingEnabled?: boolean;
        ownedDBs: Array<{
          code: string;
          name: string;
          masterDB: string;
          transDB: string;
          hostAddress: string;
          hostPort: number;
          hostName: string;
          outletName: string;
          accessAs: string[];
          renewalOnly?: boolean;
          expiredReasons?: string[];
          subscription?: OutletSubscriptionInfo;
        }>;
      };
    }>('POST', '/auth/refresh-stores', { idToken });
  }

  async logout() {
    return this.request('POST', '/auth/logout');
  }

  async getSession() {
    return this.request('GET', '/auth/session');
  }

  async searchStock(query: string, masterDB?: string, transDB?: string, registeredOnly = false) {
    // Search uses Firebase token (no session record needed) + DB metadata
    const idToken = await getIdToken(true);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    if (masterDB) {
      headers['X-POS-Master'] = masterDB;
    }
    if (transDB) {
      headers['X-POS-Transaction'] = transDB;
    }
    if (registeredOnly) {
      headers['X-POS-Registered-Only'] = '1';
    }

    const res = await this.fetchWithAuthRefresh(
      `${API_BASE}/stock?search=${encodeURIComponent(query)}`,
      { headers },
    );

    const data = await res.json();
    if (!res.ok) {
      throw new ApiError(res.status, apiErrorText(data, 'Search failed'));
    }
    return data as {
      success: boolean;
      data: Array<{
        storeName: string;
        storeCode: string;
        hostAddress: string;
        id: string;
        code: string;
        name: string;
        description: string;
        imageUrl: string;
        thumbnailUrl: string;
        allImageUrls: Array<{ url: string; imageNo: number }>;
        price: number;
        membershipPrice: number;
        packRecords: Array<{
          stock_id: string;
          pack_type: { code: string; name: string };
          pack_description: string;
          packsize: number;
          selling_price: number;
          membership_price: number;
          max_discount_percentage: number;
          percent_above_cost: number;
          barcode: string;
        }>;
        qoh: number;
        qohString: string;
        typeName: string;
      }>;
      query: string;
      totalRows: number;
      totalStores: number;
      message?: string;
    };
  }

  async uploadStockImage(storeCode: string, stockId: string, file: File): Promise<{
    success: boolean;
    data: {
      id: string;
      image_no: number;
      file_path: string;
      thumb_path: string;
      all_images: Array<{ image_no: number; file_path: string; thumb_path: string }>;
    };
  }> {
    const idToken = await getIdToken(true);

    const headers: Record<string, string> = {};
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    // Send raw binary body — the C++ gateway detects format from magic bytes.
    // Pass format hint as query param for clarity.
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace('jpeg', 'jpg');
    const res = await this.fetchWithAuthRefresh(
      `${API_BASE}/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}/images?format=${ext}`,
      { method: 'POST', headers, body: file },
    );

    const data = await res.json();
    if (!res.ok) {
      throw new ApiError(res.status, apiErrorText(data, 'Upload failed'));
    }
    return data;
  }

  async deleteStockImage(storeCode: string, stockId: string, imageNo: number): Promise<{ success: boolean }> {
    const idToken = await getIdToken(true);
    const headers: Record<string, string> = {};
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const res = await this.fetchWithAuthRefresh(
      `${API_BASE}/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}/images/${imageNo}`,
      { method: 'DELETE', headers },
    );
    const data = await res.json();
    if (!res.ok) {
      throw new ApiError(res.status, apiErrorText(data, 'Delete failed'));
    }
    return data;
  }

  /** Fetch the raw HTML for an outlet page by type (e.g. "home", "banner", "info", "announcement"). */
  async getStorePage(storeCode: string, pageType: string): Promise<{ exists: boolean; html: string }> {
    const res = await this.fetchWithAuthRefresh(
      `${API_BASE}/store/${encodeURIComponent(storeCode)}/page/${encodeURIComponent(pageType)}`,
      { method: 'GET' },
    );
    if (res.status === 404) {
      return { exists: false, html: '' };
    }
    if (!res.ok) {
      let message = 'Failed to load page';
      try { message = apiErrorText(await res.json(), message); } catch { /* ignore */ }
      throw new ApiError(res.status, message);
    }
    const html = await res.text();
    return { exists: true, html };
  }

  /** Upload a new HTML page for an outlet by type. Admin only. */
  async uploadStorePage(storeCode: string, pageType: string, file: File): Promise<{
    success: boolean;
    data: { page_type: string; file_size: number; date_updated: number; updated_by: string };
  }> {
    const idToken = await getIdToken(true);

    const headers: Record<string, string> = {};
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    // Send raw binary body — the C++ gateway reads the body directly.
    const res = await this.fetchWithAuthRefresh(
      `${API_BASE}/store/${encodeURIComponent(storeCode)}/page/${encodeURIComponent(pageType)}`,
      { method: 'POST', headers, body: file },
    );

    const data = await res.json();
    if (!res.ok) {
      throw new ApiError(res.status, apiErrorText(data, 'Upload failed'));
    }
    return data;
  }

  /** Fetch an outlet's theme settings. Returns exists:false+theme:null when no theme is set. */
  async getStoreTheme(storeCode: string): Promise<{ exists: boolean; theme: OutletTheme | null }> {
    const res = await this.fetchWithAuthRefresh(
      `${API_BASE}/store/${encodeURIComponent(storeCode)}/theme`,
      { method: 'GET' },
    );
    if (!res.ok) {
      // 404 on "store not found" — let the caller handle it
      let message = 'Failed to load theme';
      try { message = apiErrorText(await res.json(), message); } catch { /* ignore */ }
      throw new ApiError(res.status, message);
    }
    return res.json();
  }

  /** Save an outlet's theme settings. Admin only. Sends JSON body. */
  async setStoreTheme(storeCode: string, theme: OutletTheme): Promise<{
    success: boolean;
    data: { dateUpdated: number; updatedBy: string };
  }> {
    const idToken = await getIdToken(true);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const res = await this.fetchWithAuthRefresh(
      `${API_BASE}/store/${encodeURIComponent(storeCode)}/theme`,
      { method: 'PUT', headers, body: JSON.stringify(theme) },
    );

    const data = await res.json();
    if (!res.ok) {
      throw new ApiError(res.status, apiErrorText(data, 'Failed to save theme'));
    }
    return data;
  }

  async getAvailableStores() {
    return this.request<{
      success: boolean;
      data: Array<{
        code: string;
        name: string;
        masterDB: string;
        transDB: string;
        hostAddress: string;
        hostPort: number;
        hostName: string;
        hostId: number;
      }>;
    }>('GET', '/auth/available-stores');
  }

  async registerStore(
    idToken: string,
    hostAddress: string,
    hostPort: number,
    entityType: string,
  ) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${idToken}`,
    };

    const res = await this.fetchWithAuthRefresh(`${API_BASE}/auth/register-store`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ hostAddress, hostPort, entityType }),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new ApiError(res.status, apiErrorText(data, 'Registration failed'));
    }
    return data as {
      success: boolean;
      data: {
        ownedDBs?: Array<{
          code: string;
          name: string;
          masterDB: string;
          transDB: string;
          hostAddress: string;
          hostPort: number;
          hostName: string;
          outletName: string;
          accessAs: string[];
          renewalOnly?: boolean;
          expiredReasons?: string[];
        }>;
        message: string;
        needsApproval: boolean;
        requestId?: string;
      };
    };
  }

  async registerDevice(fcmToken: string) {
    // `platform` is recorded in gw_devices purely for diagnostics — knowing
    // which browser/device a token belongs to makes delivery issues traceable.
    return this.request<{ success: boolean }>('POST', '/auth/register-device', {
      fcmToken,
      platform: 'web',
    });
  }

  async unregisterDevice(fcmToken: string) {
    return this.request<{ success: boolean }>('DELETE', '/auth/register-device', { fcmToken });
  }

  /** Diagnostic: send a test push to this account's registered devices.
   *  Throws (NO_DEVICE) when this gateway has no token for the account. */
  async testNotification() {
    return this.requestChecked<{ devices: number; message: string }>('POST', '/auth/test-notification');
  }

  async getPendingApprovals() {
    return this.request<{
      success: boolean;
      data: {
        asOwner: Array<{
          id: string;
          requesterEmail: string;
          entityType: string;
          storeCode: string;
          storeName: string;
          status: 'pending' | 'approved' | 'rejected';
          createdAt: string;
          resolvedAt?: string;
        }>;
        asRequester: Array<{
          id: string;
          entityType: string;
          storeCode: string;
          storeName: string;
          status: 'pending' | 'approved' | 'rejected';
          createdAt: string;
          resolvedAt?: string;
        }>;
      };
    }>('GET', '/auth/pending-approvals');
  }

  async approveRegistration(requestId: string, idToken: string) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${idToken}`,
    };

    const res = await this.fetchWithAuthRefresh(`${API_BASE}/auth/approve-registration`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ requestId }),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new ApiError(res.status, apiErrorText(data, 'Approval failed'));
    }
    return data as {
      success: boolean;
      data: { message: string; requestId: string };
    };
  }

  async rejectRegistration(requestId: string) {
    return this.request<{
      success: boolean;
      data: { message: string; requestId: string };
    }>('POST', '/auth/reject-registration', { requestId });
  }

  // ── Profile & Health Records ──────────────────────────────────────────

  async getProfile(storeCode: string) {
    return this.request<{
      success: boolean;
      data: {
        exists: boolean;
        id?: string;
        name?: string;
        code?: string;
        email?: string;
        telNo?: string;
        address?: string;
        postcode?: string;
        state?: string;
        allergy?: string;
        medicalHistory?: string;
        longTermMedication?: string;
        notes?: string;
        dob?: number;
        gender?: string;
        bloodType?: string;
        race?: string;
        enrollNumber?: string;
        pointBalance?: number;
        message?: string;
      };
    }>('GET', `/store/${encodeURIComponent(storeCode)}/profile`);
  }

  async updateProfile(storeCode: string, data: { name?: string; email?: string }) {
    return this.request<{ success: boolean }>(
      'PUT', `/store/${encodeURIComponent(storeCode)}/profile`, data);
  }

  async getHealthObservations(storeCode: string) {
    return this.request<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/profile/health`);
  }

  async addHealthObservation(storeCode: string, data: {
    typeId: string; date: number; notes?: string;
    components: Array<{ name: string; value: string; unit: string }>;
  }) {
    return this.request<{ success: boolean; data: { id: string } }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/profile/health`, data);
  }

  async deleteHealthObservation(storeCode: string, obsId: string) {
    return this.request<{ success: boolean }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/profile/health/${encodeURIComponent(obsId)}`);
  }

  async getAllergies(storeCode: string) {
    return this.request<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/profile/allergies`);
  }

  async addAllergy(storeCode: string, data: {
    allergen: string; severityId?: string; reactionId?: string;
    notes?: string; dateRecorded?: number; active?: boolean;
  }) {
    return this.request<{ success: boolean; data: { id: string } }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/profile/allergies`, data);
  }

  async deleteAllergy(storeCode: string, allergyId: string) {
    return this.request<{ success: boolean }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/profile/allergies/${encodeURIComponent(allergyId)}`);
  }

  async getMedications(storeCode: string) {
    return this.request<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/profile/medications`);
  }

  async addMedication(storeCode: string, data: {
    drugName: string; dosage?: string; frequency?: string;
    notes?: string; startDate?: number; endDate?: number;
    prescribedBy?: string; active?: boolean;
  }) {
    return this.request<{ success: boolean; data: { id: string } }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/profile/medications`, data);
  }

  async deleteMedication(storeCode: string, medId: string) {
    return this.request<{ success: boolean }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/profile/medications/${encodeURIComponent(medId)}`);
  }

  async getMedicalHistory(storeCode: string) {
    return this.request<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/profile/history`);
  }

  async addMedicalHistory(storeCode: string, data: {
    description: string; date?: number; categoryId?: string;
    notes?: string; active?: boolean;
  }) {
    return this.request<{ success: boolean; data: { id: string } }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/profile/history`, data);
  }

  async deleteMedicalHistory(storeCode: string, histId: string) {
    return this.request<{ success: boolean }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/profile/history/${encodeURIComponent(histId)}`);
  }

  // ── Federated Purchase History (cross-chain) ──────────────────────────

  async getPurchaseHistory() {
    return this.request<{
      success: boolean;
      data: {
        items: Array<{
          id: string; date: number; refNo: string;
          quantity: number; price: number; amount: number;
          pack: string; location: string; who: string;
          storeCode: string; storeName: string;
        }>;
        total: number;
        stores: number;
      };
    }>('GET', '/purchases/history');
  }

  // ── My Sales + Vitals (federated) ─────────────────────────────────────

  async getMySales() {
    return this.request<{
      success: boolean;
      data: {
        items: Array<{
          date: number; transactionId: string; refNo: string;
          itemCode: string; item: string;
          qty: number; price: number; amount: number;
          pack: string; outletName: string; storeCode: string;
        }>;
        total: number;
      };
    }>('GET', '/sales/my');
  }

  async getVitals() {
    return this.request<{
      success: boolean;
      data: Array<{
        date: number; type: string;
        components: string[];
        outletCode: string; outletName: string;
        recordedBy: string; notes: string;
      }>;
    }>('GET', '/profile/vitals');
  }

  // ── Orders (cart → salesorder) ────────────────────────────────────────

  async createOrder(storeCode: string, data: {
    items: Array<{ stockId: string; quantity: number; packTypeId?: string; price?: number }>;
  }) {
    return this.request<{
      success: boolean;
      data: { orderId: string; totalAmount: number; itemCount: number; message: string };
    }>('POST', `/store/${encodeURIComponent(storeCode)}/orders`, data);
  }

  // ── Cart on-hold (server SuspendedSales sync) ─────────────────────────

  /** Create (no holdId) or update (holdId) the outlet on-hold receipt. */
  async cartHold(storeCode: string, data: {
    holdId?: string;
    items: Array<{ stockId: string; quantity: number }>;
  }) {
    return this.request<{
      success: boolean;
      data: { holdId: string; totalAmount: number; itemCount: number; recreated: boolean };
    }>('POST', `/store/${encodeURIComponent(storeCode)}/cart/hold`, data);
  }

  /** Release (cancel) an on-hold receipt. */
  async releaseCartHold(storeCode: string, holdId: string) {
    return this.request<{ success: boolean }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/cart/hold/${encodeURIComponent(holdId)}`);
  }

  async getOrders(storeCode: string) {
    return this.request<{
      success: boolean;
      data: Array<{ id: string; refNo: string; totalAmount: number; date: number; itemCount: number }>;
    }>('GET', `/store/${encodeURIComponent(storeCode)}/orders`);
  }

  // ── Staff Back-Office ─────────────────────────────────────────────────

  // ── Mall (mall.pharmapos.com) ─────────────────────────────────────────

  mallStaffOrders(storeCode: string, status?: string) {
    return this.request<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/mall/orders${status ? '?status=' + encodeURIComponent(status) : ''}`);
  }

  mallStaffOrderReady(storeCode: string, id: string) {
    return this.request<{ success: boolean; data: { status: string } }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/staff/mall/orders/${encodeURIComponent(id)}/ready`);
  }

  mallStaffOrderComplete(
    storeCode: string,
    id: string,
    data: { paymentMode: string; reference?: string; submitEInvoice?: boolean },
  ) {
    return this.request<{ success: boolean; data: { status: string; saleId: string } }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/staff/mall/orders/${encodeURIComponent(id)}/complete`, data);
  }

  mallStaffOrderCancel(storeCode: string, id: string) {
    return this.request<{ success: boolean; data: { status: string; message?: string } }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/staff/mall/orders/${encodeURIComponent(id)}/cancel`);
  }

  mallListings(storeCode: string) {
    return this.requestChecked<any[]>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/mall/listings`);
  }

  /** Gateway-owned mall category tree (flat list; group by parentId). */
  mallCategories() {
    return this.requestChecked<MallCategory[]>('GET', '/mall/categories');
  }

  mallPublish(storeCode: string, data: {
    stockId: string;
    packTypeId?: string;
    days?: number;
    endDate?: number;
    noEnd?: boolean;
    note?: string;
    categoryIds: string[];
  }) {
    return this.requestChecked<{ ok: boolean; listingEnd: number }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/staff/mall/listings`, data);
  }

  mallUnpublish(storeCode: string, stockId: string) {
    return this.requestChecked<{ ok: boolean; message: string }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/staff/mall/listings/${encodeURIComponent(stockId)}`);
  }

  // ── Payment gateways (Billplz / ToyyibPay) ───────────────────────────
  paymentGetSettings(storeCode: string) {
    return this.requestChecked<{
      activeProvider: string;
      providers: Array<{
        provider: string;
        enabled: boolean;
        sandbox: boolean;
        collectionId: string;
        categoryCode: string;
        categoryName: string;
        hasSecretKey: boolean;
        hasSignatureKey: boolean;
        configured: boolean;
        fee: { mode: string; percent: number; fixedCents: number; minCents: number; maxCents: number };
        lastTestOk: boolean;
        lastTestMessage: string;
        lastTestedAt: number;
      }>;
    }>('GET', `/store/${encodeURIComponent(storeCode)}/staff/outlet/payment`);
  }

  paymentSetSettings(storeCode: string, data: {
    activeProvider: string;
    providers: Array<{
      provider: string;
      enabled?: boolean;
      sandbox: boolean;
      collectionId?: string;
      categoryCode?: string;
      categoryName?: string;
      secretKey?: string;
      signatureKey?: string;
      fee: { mode: string; percent: number; fixedCents: number; minCents: number; maxCents: number };
    }>;
  }) {
    return this.requestChecked<{ ok: boolean }>(
      'PUT', `/store/${encodeURIComponent(storeCode)}/staff/outlet/payment`, data);
  }

  paymentTest(storeCode: string, data: {
    provider: string;
    sandbox: boolean;
    collectionId?: string;
    categoryCode?: string;
    secretKey?: string;
    signatureKey?: string;
  }) {
    return this.requestChecked<{ ok: boolean; message: string; provider: string }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/staff/outlet/payment/test`, data);
  }

  paymentCreateCategory(storeCode: string, data: {
    provider: string;
    sandbox: boolean;
    categoryName: string;
    categoryDescription?: string;
    secretKey?: string;
  }) {
    return this.requestChecked<{
      ok: boolean;
      message: string;
      categoryCode: string;
      categoryName: string;
    }>('POST', `/store/${encodeURIComponent(storeCode)}/staff/outlet/payment/category`, data);
  }

  paymentCreateCollection(storeCode: string, data: {
    provider: string;
    sandbox: boolean;
    collectionName: string;
    secretKey?: string;
  }) {
    return this.requestChecked<{
      ok: boolean;
      message: string;
      collectionId: string;
      collectionName: string;
    }>('POST', `/store/${encodeURIComponent(storeCode)}/staff/outlet/payment/collection`, data);
  }

  payments(storeCode: string, params: {
    status?: string;
    provider?: string;
    from?: string;
    to?: string;
    limit?: number;
  } = {}) {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.provider) q.set('provider', params.provider);
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.limit) q.set('limit', String(params.limit));
    return this.requestChecked<Array<Record<string, unknown>>>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/payments${q.toString() ? '?' + q.toString() : ''}`);
  }

  /** Current outlet location (GlobalData.db) + the registered address as a hint. */
  getOutletLocation(storeCode: string) {
    return this.requestChecked<{
      outletCode: string;
      outletName: string;
      location: string;
      hasCoordinates: boolean;
      latitude: number;
      longitude: number;
      outletAddress: string;
    }>('GET', `/store/${encodeURIComponent(storeCode)}/staff/outlet/location`);
  }

  /** Save the outlet location / GPS coordinates (admin only). */
  setOutletLocation(storeCode: string, data: {
    location: string;
    hasCoordinates: boolean;
    latitude: number;
    longitude: number;
  }) {
    return this.requestChecked<{ ok: boolean }>(
      'PUT', `/store/${encodeURIComponent(storeCode)}/staff/outlet/location`, data);
  }

  staffStock(storeCode: string, search?: string) {
    return this.request<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/stock${search ? '?search=' + encodeURIComponent(search) : ''}`);
  }

  staffSuppliers(storeCode: string, search?: string) {
    return this.request<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/suppliers${search ? '?search=' + encodeURIComponent(search) : ''}`);
  }

  staffMembers(storeCode: string, search?: string) {
    return this.request<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/members${search ? '?search=' + encodeURIComponent(search) : ''}`);
  }

  staffTxByItem(storeCode: string, stockId: string) {
    return this.request<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/transactions/by-item/${encodeURIComponent(stockId)}`);
  }

  staffTxBySupplier(storeCode: string, supplierId: string) {
    return this.request<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/transactions/by-supplier/${encodeURIComponent(supplierId)}`);
  }

  staffPOs(storeCode: string) {
    return this.request<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/purchase-orders`);
  }

  staffCreatePO(storeCode: string, data: { refNo?: string; supplierId?: string; items: Array<{ stockId: string; quantity: number; price: number }> }) {
    return this.request<{ success: boolean; data: { id: string; totalAmount: number } }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/staff/purchase-orders`, data);
  }

  staffSales(storeCode: string) {
    return this.request<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/sales`);
  }

  staffHoldSale(storeCode: string, data: { refNo?: string; items: Array<{ stockId: string; quantity: number; price: number }> }) {
    return this.request<{ success: boolean; data: { id: string; totalAmount: number; status: string } }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/staff/sales/hold`, data);
  }

  /**
   * Trade endpoints answer HTTP 200 even for failures
   * ({success:false, error:{code,message}}) — surface those as ApiError so
   * panels never mistake an error response for data (e.g. an undefined
   * server key silently disabling the POS).
   */
  private async requestTrade<T>(method: string, path: string, body?: unknown): Promise<{ success: boolean; data: T }> {
    const res = await this.requestWithAuth<{ success: boolean; data: T }>(method, path, body);
    if (!res || res.success === false || res.data === undefined) {
      throw new ApiError(200, apiErrorText(res, 'Request failed'));
    }
    return res;
  }

  // ── Trade: server key / POS item lookup ───────────────────────────────

  /** Server-key subset the trade panels need (sales types, rounding, pay-by list). */
  tradeServerKey(storeCode: string) {
    return this.requestTrade<TradeServerKey>(
      'GET', `/store/${encodeURIComponent(storeCode)}/config/serverkey`);
  }

  /** POS lookup by stock id — full detail incl. cost fields + packs. */
  posStockById(storeCode: string, id: string) {
    return this.requestTrade<PosStockItem>(
      'GET', `/store/${encodeURIComponent(storeCode)}/pos/stock?id=${encodeURIComponent(id)}`);
  }

  /** POS lookup by typed code / receive-id / barcode (desktop grid parity). */
  posStockByCode(storeCode: string, code: string) {
    return this.requestTrade<PosStockItem>(
      'GET', `/store/${encodeURIComponent(storeCode)}/pos/stock?code=${encodeURIComponent(code)}`);
  }

  /** POS item search for the picker (light rows, no cost). */
  posStockSearch(storeCode: string, search: string) {
    return this.requestTrade<PosStockItem[]>(
      'GET', `/store/${encodeURIComponent(storeCode)}/pos/stock?search=${encodeURIComponent(search)}`);
  }

  // ── Trade: sales (receipts / invoices / holds) ────────────────────────

  /** Receipt / invoice / on-hold lists. Dates are epoch ms (wide default). */
  tradeSalesList(storeCode: string, opts: {
    type: 'cash' | 'credit';
    method?: 'sales' | 'suspend';
    from?: number;
    to?: number;
  }) {
    const q = new URLSearchParams({ type: opts.type, method: opts.method ?? 'sales' });
    if (opts.from !== undefined) q.set('from', String(opts.from));
    if (opts.to !== undefined) q.set('to', String(opts.to));
    return this.requestTrade<TradeSalesRow[]>(
      'GET', `/store/${encodeURIComponent(storeCode)}/sales?${q.toString()}`);
  }

  /** Sale detail incl. lines + payments. */
  tradeSalesDetail(storeCode: string, id: string, method: 'sales' | 'suspend' = 'sales') {
    return this.requestTrade<TradeSalesRow>(
      'GET', `/store/${encodeURIComponent(storeCode)}/sales/${encodeURIComponent(id)}?method=${method}`);
  }

  /** Complete a sale (posts to Sales — stock deduction, points, credit terms). */
  tradeCompleteSale(storeCode: string, data: TradeSaleIn) {
    return this.requestTrade<{ id: string; seqNo: number; totalAmount: number; taxValue: number; rounding: number; status: string }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/sales`, data);
  }

  /** Hold a receipt (SuspendedSales — reserves stock via qohHoldOut). */
  tradeHoldSale(storeCode: string, data: TradeSaleIn) {
    return this.requestTrade<{ id: string; seqNo: number; totalAmount: number; status: string }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/sales/hold`, data);
  }

  /** Delete a posted sale or release an on-hold receipt. */
  tradeDeleteSale(storeCode: string, id: string, method: 'sales' | 'suspend' = 'sales') {
    return this.requestTrade<{ id: string }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/sales/${encodeURIComponent(id)}?method=${method}`);
  }

  /** Invoice / delivery-order / proforma PDF for a sale. */
  tradeSalesInvoicePdf(storeCode: string, id: string, opts: {
    format?: 'invoice' | 'delivery-note' | 'poison-order' | 'proforma-invoice';
    method?: 'sales' | 'suspend';
  } = {}) {
    const q = new URLSearchParams();
    if (opts.format) q.set('format', opts.format);
    if (opts.method === 'suspend') q.set('method', 'suspend');
    return this.fetchTradeDocument(
      `/store/${encodeURIComponent(storeCode)}/sales/${encodeURIComponent(id)}/invoice?${q.toString()}`);
  }

  /** Sales summary PDF (cash = retail, credit = wholesale). */
  tradeSalesSummaryPdf(storeCode: string, opts: { type: 'cash' | 'credit'; from: number; to: number }) {
    const q = new URLSearchParams({ type: opts.type, from: String(opts.from), to: String(opts.to) });
    return this.fetchTradeDocument(`/store/${encodeURIComponent(storeCode)}/sales/summary?${q.toString()}`);
  }

  // ── Trade: purchases (receive stock / holds) ──────────────────────────

  /** Receive-stock / on-hold receive lists. */
  tradePurchaseList(storeCode: string, opts: { kind: 'receive' | 'hold'; from?: number; to?: number }) {
    const q = new URLSearchParams({ kind: opts.kind });
    if (opts.from !== undefined) q.set('from', String(opts.from));
    if (opts.to !== undefined) q.set('to', String(opts.to));
    return this.requestTrade<TradePurchaseRow[]>(
      'GET', `/store/${encodeURIComponent(storeCode)}/purchases?${q.toString()}`);
  }

  /** Receive detail incl. lines. */
  tradePurchaseDetail(storeCode: string, id: string, kind: 'receive' | 'hold' = 'receive') {
    return this.requestTrade<TradePurchaseRow>(
      'GET', `/store/${encodeURIComponent(storeCode)}/purchases/${encodeURIComponent(id)}?kind=${kind}`);
  }

  /** Post receive stock (QOH up, cost + selling price update, PO knock-off). */
  tradeReceiveStock(storeCode: string, data: TradePurchaseIn) {
    return this.requestTrade<{ id: string; status: string }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/purchases`, data);
  }

  /** Hold receive stock (SuspendedReceive — reserves stock via qohHoldIn). */
  tradeHoldReceive(storeCode: string, data: TradePurchaseIn) {
    return this.requestTrade<{ id: string; status: string }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/purchases/hold`, data);
  }

  /** Delete a posted receive or release an on-hold receive. */
  tradeDeletePurchase(storeCode: string, id: string, kind: 'receive' | 'hold' = 'receive') {
    return this.requestTrade<{ id: string }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/purchases/${encodeURIComponent(id)}?kind=${kind}`);
  }

  /** Purchase PDF: posted receive ("invoice"), purchase order ("po") or on-hold DRAFT invoice ("draft"). */
  tradePurchasePdf(storeCode: string, id: string, format: 'invoice' | 'po' | 'draft' = 'invoice') {
    return this.fetchTradeDocument(
      `/store/${encodeURIComponent(storeCode)}/purchases/${encodeURIComponent(id)}/pdf?format=${format}`);
  }

  /** Purchase summary PDF. */
  tradePurchaseSummaryPdf(storeCode: string, opts: { from: number; to: number }) {
    const q = new URLSearchParams({ from: String(opts.from), to: String(opts.to) });
    return this.fetchTradeDocument(`/store/${encodeURIComponent(storeCode)}/purchases/summary?${q.toString()}`);
  }

  /**
   * GET a binary trade document (PDF). Throws ApiError with the server
   * message when the gateway answers with JSON instead of a PDF.
   */
  private async fetchTradeDocument(url: string): Promise<Blob> {
    const res = await this.fetchWithAuthRefresh(`${API_BASE}${url}`, { method: 'GET' });
    const contentType = res.headers.get('Content-Type') || '';
    if (!res.ok || contentType.includes('application/json')) {
      let message = `Document failed (${res.status})`;
      try {
        const data = await res.json();
        message = data?.error?.message || message;
      } catch { /* keep default message */ }
      throw new ApiError(res.status, message);
    }
    return res.blob();
  }

  // ── Member back-office (staff admin panel) ────────────────────────────

  staffMemberDetail(storeCode: string, id: string) {
    return this.request<{ success: boolean; data: MemberDetail }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/members/${encodeURIComponent(id)}`);
  }

  staffMemberCreate(storeCode: string, data: Partial<MemberDetail>) {
    return this.request<{ success: boolean; data: MemberDetail }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/staff/members`, data);
  }

  staffMemberUpdate(storeCode: string, id: string, data: Partial<MemberDetail>) {
    return this.request<{ success: boolean; data: MemberDetail }>(
      'PUT', `/store/${encodeURIComponent(storeCode)}/staff/members/${encodeURIComponent(id)}`, data);
  }

  staffMemberDelete(storeCode: string, id: string) {
    return this.request<{ success: boolean; data: { id: string } }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/staff/members/${encodeURIComponent(id)}`);
  }

  staffMemberPoints(storeCode: string, id: string) {
    return this.request<{ success: boolean; data: MemberPointRow[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/members/${encodeURIComponent(id)}/points`);
  }

  staffMemberHistory(storeCode: string, id: string) {
    return this.request<{ success: boolean; data: HistoryRow[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/members/${encodeURIComponent(id)}/history`);
  }

  staffMemberAccount(storeCode: string, id: string) {
    return this.request<{ success: boolean; data: AccountRow[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/members/${encodeURIComponent(id)}/account`);
  }

  // ── Company back-office (customers / suppliers / sales reps / promoters) ──

  staffCompanies(storeCode: string, type: CompanyType, search?: string) {
    const qs = `?type=${encodeURIComponent(type)}` +
      (search ? `&search=${encodeURIComponent(search)}` : '');
    return this.request<{ success: boolean; data: CompanyListRow[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/companies${qs}`);
  }

  staffCompanyDetail(storeCode: string, type: CompanyType, id: string) {
    return this.request<{ success: boolean; data: CompanyDetail }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/companies/${encodeURIComponent(id)}?type=${encodeURIComponent(type)}`);
  }

  staffCompanyCreate(storeCode: string, type: CompanyType, data: Partial<CompanyDetail>) {
    return this.request<{ success: boolean; data: CompanyDetail }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/staff/companies?type=${encodeURIComponent(type)}`, data);
  }

  staffCompanyUpdate(storeCode: string, type: CompanyType, id: string, data: Partial<CompanyDetail>) {
    return this.request<{ success: boolean; data: CompanyDetail }>(
      'PUT', `/store/${encodeURIComponent(storeCode)}/staff/companies/${encodeURIComponent(id)}?type=${encodeURIComponent(type)}`, data);
  }

  staffCompanyDelete(storeCode: string, type: CompanyType, id: string) {
    return this.request<{ success: boolean; data: { id: string } }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/staff/companies/${encodeURIComponent(id)}?type=${encodeURIComponent(type)}`);
  }

  staffCompanyHistory(storeCode: string, type: CompanyType, id: string) {
    return this.request<{ success: boolean; data: HistoryRow[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/companies/${encodeURIComponent(id)}/history?type=${encodeURIComponent(type)}`);
  }

  staffCompanySupplyList(storeCode: string, id: string) {
    return this.request<{ success: boolean; data: SupplyRow[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/companies/${encodeURIComponent(id)}/supply-list`);
  }

  staffCompanyItems(storeCode: string, type: CompanyType, id: string) {
    return this.request<{ success: boolean; data: QuotationRow[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/companies/${encodeURIComponent(id)}/items?type=${encodeURIComponent(type)}`);
  }

  staffCompanyAccount(storeCode: string, id: string, detail = false) {
    return this.request<{ success: boolean; data: AccountRow[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/companies/${encodeURIComponent(id)}/account${detail ? '/detail' : ''}`);
  }

  staffCompanyInvoices(storeCode: string, type: CompanyType, id: string) {
    return this.request<{ success: boolean; data: InvoiceRow[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/companies/${encodeURIComponent(id)}/invoices?type=${encodeURIComponent(type)}`);
  }

  staffCompanySummary(storeCode: string, type: CompanyType, id: string) {
    return this.request<{ success: boolean; data: SummaryRow[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/companies/${encodeURIComponent(id)}/summary?type=${encodeURIComponent(type)}`);
  }

  // ── Stock Management (Admin write / Staff read) ───────────────────────

  /** Store-scoped stock search for the management page (staff access). */
  stockManageSearch(storeCode: string, search?: string) {
    return this.requestWithAuth<{ success: boolean; data: StockListItem[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/staff/stock${search ? '?search=' + encodeURIComponent(search) : ''}`);
  }

  /** Full stock record incl. packs, taxes, promotions, components, shelves. */
  getStockDetail(storeCode: string, stockId: string) {
    return this.requestWithAuth<{ success: boolean; data: StockDetail }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}`);
  }

  /** Server key flags + stock types, adjust types, price categories, categories. Admin only. */
  getStockMeta(storeCode: string) {
    return this.requestWithAuth<{ success: boolean; data: StockMeta }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/stock/meta`);
  }

  /** Next auto-generated stock code for a type. Admin only. */
  getStockNextCode(storeCode: string, typeId: string) {
    return this.requestWithAuth<{ success: boolean; data: { code: string } }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/stock/next-code?typeId=${encodeURIComponent(typeId)}`);
  }

  /** Create a new stock item. Admin only. */
  createStock(storeCode: string, payload: StockSavePayload) {
    return this.requestWithAuth<{ success: boolean; data: StockDetail }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/stock`, payload);
  }

  /** Update a stock item (send the full echoed record). Admin only. */
  updateStock(storeCode: string, stockId: string, payload: StockSavePayload) {
    return this.requestWithAuth<{ success: boolean; data: StockDetail }>(
      'PUT', `/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}`, payload);
  }

  /** Adjust QOH by a delta (smallest-pack units). Admin only. */
  adjustStockQoh(storeCode: string, stockId: string, payload: {
    quantity: number;
    adjustTypeId: string;
    packTypeId: string;
    packSize: number;
    unitCost: number;
    costValue: number;
  }) {
    return this.requestWithAuth<{ success: boolean; data: { id: string; diff: number } }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}/qoh`, payload);
  }

  /** Soft-delete a stock item. Admin only. */
  deleteStock(storeCode: string, stockId: string) {
    return this.requestWithAuth<{ success: boolean }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}`);
  }

  /** Hide (deactivate) a stock item. Admin only. */
  hideStock(storeCode: string, stockId: string) {
    return this.requestWithAuth<{ success: boolean }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}/hide`);
  }

  /** Monthly inventory status summary. */
  getStockInventory(storeCode: string, stockId: string) {
    return this.requestWithAuth<{ success: boolean; data: Array<{
      month: string; quantityIn: number; quantityOutRetail: number;
      quantityOutWholesales: number; quantityAdj: number; runningTotal: number;
    }> }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}/inventory`);
  }

  /** Sales history for a stock item. */
  getStockSalesHistory(storeCode: string, stockId: string) {
    return this.requestWithAuth<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}/history/sales`);
  }

  /** Purchase history for a stock item. */
  getStockPurchaseHistory(storeCode: string, stockId: string) {
    return this.requestWithAuth<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}/history/purchases`);
  }

  /** Stock adjustment (transaction) log for a stock item. */
  getStockTransactions(storeCode: string, stockId: string) {
    return this.requestWithAuth<{ success: boolean; data: any[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}/transactions`);
  }

  /** Delete a stock adjustment. Admin only. */
  deleteStockTransaction(storeCode: string, stockId: string, transactionId: string) {
    return this.requestWithAuth<{ success: boolean }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}/transactions/${encodeURIComponent(transactionId)}`);
  }

  /** List images for a stock item (metadata incl. DownloadFile paths). */
  getStockImages(storeCode: string, stockId: string) {
    return this.requestWithAuth<{ success: boolean; data: StockImageEntry[] }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/stock/${encodeURIComponent(stockId)}/images`);
  }

  // ── Reports (Admin-only "Other Reports") ──────────────────────────────

  /** Report menu tree (parsed from the server's ServerSideMenu.xml). */
  getReportMenu(storeCode: string) {
    return this.requestWithAuth<{ success: boolean; data: ReportMenuNode }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/reports/menu`);
  }

  /** Entity/product lookup for report dialog pickers. */
  searchReportLookup(storeCode: string, type: string, q: string, limit = 30) {
    return this.requestWithAuth<{ success: boolean; data: { results: ReportLookupItem[] } }>(
      'GET', `/store/${encodeURIComponent(storeCode)}/reports/lookup` +
      `?type=${encodeURIComponent(type)}&q=${encodeURIComponent(q)}&limit=${limit}`);
  }

  /**
   * Run a report and download the generated file (XLS/PDF).
   * Returns the binary blob; throws ApiError with the server message on failure.
   * POST is used so the service worker never caches it.
   */
  async runReport(storeCode: string, payload: ReportRunPayload): Promise<Blob> {
    const res = await this.fetchWithAuthRefresh(
      `${API_BASE}/store/${encodeURIComponent(storeCode)}/reports/run`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

    const contentType = res.headers.get('Content-Type') || '';
    if (!res.ok || contentType.includes('application/json')) {
      let message = `Report failed (${res.status})`;
      try {
        const data = await res.json();
        message = data?.error?.message || message;
      } catch { /* keep default message */ }
      throw new ApiError(res.status, message);
    }
    return res.blob();
  }

  // ── Accounting ───────────────────────────────────────────────────────

  private async requestAccounting<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<{ success: boolean; data: T }> {
    const res = await this.requestWithAuth<{ success: boolean; data: T }>(method, path, body);
    if (!res || res.success === false || res.data === undefined) {
      throw new ApiError(200, apiErrorText(res, 'Request failed'));
    }
    return res;
  }

  private accountingQuery(params: Record<string, string | number | undefined>): string {
    const q = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&');
    return q ? `?${q}` : '';
  }

  accountingMeta(storeCode: string) {
    return this.requestAccounting<AccountingMeta>(
      'GET', `/store/${encodeURIComponent(storeCode)}/accounting/meta`);
  }

  accountingSetPayByAccount(storeCode: string, paidById: string, kind: string, accountId: number) {
    return this.requestAccounting<{ paidById: string; kind: string; accountId: number }>(
      'PUT', `/store/${encodeURIComponent(storeCode)}/accounting/payby-accounts`,
      { paidById, kind, accountId });
  }

  accountingAccountCreate(storeCode: string, body: { typeId: string; code: string; name: string }) {
    return this.requestAccounting<{ id: string }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/accounting/accounts`, body);
  }

  accountingAccountUpdate(storeCode: string, id: string, body: { code: string; name: string }) {
    return this.requestAccounting<{ id: string }>(
      'PUT', `/store/${encodeURIComponent(storeCode)}/accounting/accounts/${encodeURIComponent(id)}`, body);
  }

  accountingAccountDelete(storeCode: string, id: string) {
    return this.requestAccounting<{ id: string }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/accounting/accounts/${encodeURIComponent(id)}`);
  }

  accountingExpenses(
    storeCode: string,
    params: { from: number; to: number; category?: string; paidBy?: string; q?: string; limit?: number },
  ) {
    return this.requestAccounting<ExpenseListResult>(
      'GET', `/store/${encodeURIComponent(storeCode)}/accounting/expenses` + this.accountingQuery(params));
  }

  accountingExpenseSave(storeCode: string, body: ExpenseInput) {
    return this.requestAccounting<{ id: string }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/accounting/expenses`, body);
  }

  accountingExpenseDelete(storeCode: string, id: string) {
    return this.requestAccounting<{ id: string }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/accounting/expenses/${encodeURIComponent(id)}`);
  }

  accountingAging(
    storeCode: string,
    kind: 'receivables' | 'payables',
    params: { from: number; to: number; asOf?: number },
  ) {
    return this.requestAccounting<AgingResult>(
      'GET', `/store/${encodeURIComponent(storeCode)}/accounting/${kind}` + this.accountingQuery(params));
  }

  accountingRecordReceipt(storeCode: string, body: PartyPaymentInput) {
    return this.requestAccounting<PartyPaymentResult>(
      'POST', `/store/${encodeURIComponent(storeCode)}/accounting/receipts`, body);
  }

  accountingRecordPayment(storeCode: string, body: PartyPaymentInput) {
    return this.requestAccounting<PartyPaymentResult>(
      'POST', `/store/${encodeURIComponent(storeCode)}/accounting/payments`, body);
  }

  accountingSettlements(storeCode: string, params: { from: number; to: number }) {
    return this.requestAccounting<SettlementsResult>(
      'GET', `/store/${encodeURIComponent(storeCode)}/accounting/settlements` + this.accountingQuery(params));
  }

  accountingSettlementSave(
    storeCode: string,
    body: {
      kind: 'opening' | 'payout' | 'deposit' | 'adjustment';
      date: number;
      fromAccountId?: number;
      toAccountId: number;
      amount: number;
      feeAmount?: number;
      paidById?: string;
      provider?: string;
      reference?: string;
      remark?: string;
      periodFrom?: number;
      periodTo?: number;
    },
  ) {
    return this.requestAccounting<{ id: string }>(
      'POST', `/store/${encodeURIComponent(storeCode)}/accounting/settlements`, body);
  }

  accountingSettlementDelete(storeCode: string, id: number | string) {
    return this.requestAccounting<{ id: string }>(
      'DELETE', `/store/${encodeURIComponent(storeCode)}/accounting/settlements/${encodeURIComponent(String(id))}`);
  }

  accountingReport<T = any>(
    storeCode: string,
    report: string,
    params: { from: number; to: number; chartFrom?: number; asOf?: number; partyId?: string; partyType?: string },
  ) {
    return this.requestAccounting<T>(
      'GET',
      `/store/${encodeURIComponent(storeCode)}/accounting/reports` +
        this.accountingQuery({ report, ...params }),
    );
  }

  /** Compact financial-dashboard payload for one outlet (Mobile-Dashboard role). */
  async accountingDashboard(
    storeCode: string,
    params: { from: number; to: number; chartFrom: number; asOf: number },
  ): Promise<DashboardResult> {
    const res = await this.accountingReport<any>(storeCode, 'dashboard', params);
    return res.data as DashboardResult;
  }

  /** Parties + invoices making up a clicked AR/AP ageing bucket. */
  accountingDashboardAging(
    storeCode: string,
    params: { kind: 'ar' | 'ap'; bucket: AgingBucket; asOf: number },
  ) {
    return this.requestAccounting<DashboardAgingDetail>(
      'GET',
      `/store/${encodeURIComponent(storeCode)}/accounting/reports` +
        this.accountingQuery({ report: 'dashboard-aging', ...params }),
    );
  }

  /** Stock items making up a clicked stock-control KPI. */
  accountingDashboardStock(
    storeCode: string,
    params: { kind: StockControlKind; asOf: number; limit?: number },
  ) {
    return this.requestAccounting<DashboardStockDetail>(
      'GET',
      `/store/${encodeURIComponent(storeCode)}/accounting/reports` +
        this.accountingQuery({ report: 'dashboard-stock', ...params }),
    );
  }

  // ── Subscription billing (gateway owner's ToyyibPay account) ─────────
  billingOutlets() {
    return this.requestChecked<{
      billingEnabled?: boolean;
      pricing: BillingPricing;
      outlets: BillingOutletView[];
      message?: string;
    }>('GET', '/billing/outlets');
  }

  billingCheckout(outletUuid: string, kind: 'gateway' | 'db', cycle: 'monthly' | 'yearly') {
    return this.requestChecked<{
      invoiceId: string;
      paymentUrl: string;
      amountCents: number;
      currency: string;
    }>('POST', '/billing/checkout', { outletUuid, kind, cycle });
  }

  billingRequery(invoiceId: string) {
    return this.requestChecked<BillingInvoice>('POST', '/billing/requery', { invoiceId });
  }

  billingInvoices(outletUuid: string) {
    return this.requestChecked<BillingInvoice[]>(
      'GET', `/billing/invoices?outletUuid=${encodeURIComponent(outletUuid)}`);
  }
}

export interface BillingPricing {
  currency: string;
  gateway: { monthlyCents: number; yearlyCents: number };
  db: { monthlyCents: number; yearlyCents: number };
}

export interface BillingInvoice {
  id: string;
  outletUuid: string;
  storeCode: string;
  kind: 'gateway' | 'db';
  cycle: 'monthly' | 'yearly';
  months: number;
  amountCents: number;
  currency: string;
  status: string;
  createdAt: number;
  paidAt: number;
  appliedAt: number;
  periodStart: number;
  periodEnd: number;
  lastError?: string;
  paymentUrl?: string;
}

export interface BillingOutletView {
  /** False when the gateway has no checkout configured; expiry is still shown. */
  billingEnabled?: boolean;
  uuid: string;
  code: string;
  name: string;
  serverId: string;
  serverName: string;
  serverOnline: boolean;
  canRenew: boolean;
  declared: boolean;
  plan: string;
  gateway: {
    subscribed: boolean;
    status: string;
    daysLeft: number;
    expiresAt: number;
  };
  db: {
    enforced: boolean;
    subscribed: boolean;
    forever: boolean;
    status: string;
    daysLeft: number;
    expiresAt: number;
  };
  pendingInvoice?: BillingInvoice;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** One gateway-owned mall category or subcategory. */
export interface MallCategory {
  id: string;
  parentId: string;
  name: string;
  slug: string;
  sortOrder: number;
  system?: boolean;
  itemCount?: number;
}

export const api = new ApiClient();
